import { localPoint } from '@visx/event';
import type { UseTooltipParams } from '@visx/tooltip/lib/hooks/useTooltip';
import type { Dispatch, SetStateAction } from 'react';
import { accessor } from '@/components/Charts/utils/accessor';
import { getClosestLine } from '@/components/Charts/XYCharts/LineChart/utils/getClosestLine';
import type { Data } from '@/types/chart';
import type { getScales } from '../../utils/getScales';
import type { LineChartValues } from '../types/LineChartValues';

type ShowTooltip = UseTooltipParams<{ data: Data }>['showTooltip'];

const createChartHandlers = (
  setCirclePos: (pos: { x: number; y: number } | null) => void,
  showTooltip: ShowTooltip,
  scales: ReturnType<typeof getScales>,
  setChartData: Dispatch<SetStateAction<LineChartValues[][]>>,
) => {
  const setHovered = (id: number | string | null, groupIndex: number | null) => {
    setChartData((prev) => {
      // Clear all hovered
      const cleared = prev.map((group) => group.map((item) => ({ ...item, hovered: false })));

      if (id === null || id === undefined || groupIndex === null || groupIndex === undefined) {
        return cleared;
      }

      const idx = cleared[groupIndex].findIndex((d) => d.id === id);
      if (idx < 0) return cleared;

      cleared[groupIndex][idx].hovered = true;
      return cleared;
    });
  };

  const getClosest = (event: React.MouseEvent<SVGPathElement, MouseEvent>, data: LineChartValues[]) => {
    const mousePos = localPoint(event) || { x: 0, y: 0 };
    return getClosestLine(data, accessor, scales.xScale, scales.yScale, scales.tickLength, mousePos);
  };

  const handleHover = (closestLine: ReturnType<typeof getClosest>) => {
    if (closestLine) {
      setHovered(closestLine.data.id, closestLine.data.groupIndex);
    }
  };

  const handleCirclePost = (closestLine: ReturnType<typeof getClosest>) => {
    if (closestLine) {
      setCirclePos(closestLine.data.value ? { x: closestLine.coords.x, y: closestLine.coords.y } : null);
    }
  };

  const handleTooltip = (closestLine: ReturnType<typeof getClosest>) => {
    if (closestLine) {
      showTooltip({
        tooltipLeft: closestLine.coords.x,
        tooltipTop: closestLine.coords.y,
        tooltipData: { data: closestLine.data },
      });
    }
  };

  const handleLegendClick = (i: number, groupIndex: number) => {
    setChartData((prev) => {
      const next = prev.map((group, gi) =>
        group.map((item, li) => (gi === groupIndex && li === i ? { ...item, visible: !item.visible } : item)),
      );

      const visible = next.flat(1).filter((d) => d.visible);

      // Ensure at least one visible; if none, revert toggle
      if (visible.length < 1) {
        return prev;
      }

      // If there's only one visible, mark it hovered; else clear hovered in that group
      if (visible.length === 1) {
        const only = visible[0];
        return next.map((group, gi) =>
          group.map((item) => ({
            ...item,
            hovered: !!(gi === only.groupIndex && item.id === only.id),
          })),
        );
      } else {
        // Clear hovered in the affected group only
        return next.map((group, gi) =>
          gi === groupIndex ? group.map((item) => ({ ...item, hovered: false })) : group,
        );
      }
    });
  };

  return {
    setHovered,
    handleHover,
    handleCirclePost,
    handleTooltip,
    handleLegendClick,
    getClosest,
  };
};

export { createChartHandlers };
